import { makeStyles, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import {
    compareAsc,
    differenceInCalendarMonths,
    eachMonthOfInterval,
    endOfMonth,
    isWithinInterval,
    startOfMonth,
} from 'date-fns';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../forms/Button';
import { CalendarContext, CalendarEvent } from './Calendar';
import { Event } from './Event';
import { useMonthClasses as useYearMonthClasses } from './YearView';

const useQuarterViewClasses = makeStyles({
    root: {
        display: 'flex',
        flex: 1,
        gap: 2,
        '& > div': { flex: '1 0 0', width: 0 },
    },
});

/**
 * Calendar quarter view.
 *
 * @param props.context calendar context
 */
export const QuarterView = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useQuarterViewClasses();
    const { events, intervals, onFilter } = React.useContext(props.context);
    const quarterEvents = React.useMemo(
        () => events.filter(event => isWithinInterval(event.interval.start, intervals.quarter)),
        [events, +intervals.quarter.start, +intervals.quarter.end],
    );
    const dragEvent = React.useRef<CalendarEvent>();

    React.useEffect(() => onFilter?.(quarterEvents), [onFilter, quarterEvents]);

    return (
        <div className={classes.root}>
            {eachMonthOfInterval(intervals.quarter).map(month => (
                <Month key={+month} {...props} events={quarterEvents} month={month} dragEvent={dragEvent} />
            ))}
        </div>
    );
};

const useMonthClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        '&.drag-over': {
            '& > *': { pointerEvents: 'none' },
            '& > button': { boxShadow: `0 0 4px 2px ${theme.colors.neutral[3]} inset` },
        },
    },
    header: {
        width: '100%',
        justifyContent: 'flex-start',
        borderBottom: `1px solid ${theme.colors.neutral[6]}`,
        borderRadius: 0,
    },
    events: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        '@supports (overflow: overlay)': { overflow: 'overlay' },
        flex: '1 0 0',
        gap: 2,
        '& > span': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            padding: 8,
            color: theme.colors.neutral[2],
            backgroundColor: 'white',
            mixBlendMode: 'normal',
            ...theme.fonts.h6,
            textTransform: 'uppercase',
        },
    },
});

/**
 * Render a month section of the year view.
 *
 * @param props.context calendar context
 * @param props.events events to render
 * @param props.month date inside the month to be rendered
 * @param props.dragEvent event being dragged
 * @param props.setDragEvent set event being dragged
 */
const Month = (props: {
    context: React.Context<CalendarContext>;
    events: CalendarEvent[];
    month: Date;
    dragEvent: React.MutableRefObject<CalendarEvent | undefined>;
}) => {
    const classes = useMonthClasses();
    const yearMonthClasses = useYearMonthClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.QuarterView' });
    const { setView, setAnchor, onSelected, onDnD, onExternalDnD, groupSort } = React.useContext(props.context);
    const interval = { start: startOfMonth(props.month), end: endOfMonth(props.month) };
    const monthEvents = React.useMemo(
        () => props.events.filter(event => isWithinInterval(event.interval.start, interval)),
        [props.events, +interval.start, +interval.end],
    );
    const groupedMonthEvents = React.useMemo(
        () =>
            monthEvents.reduce<{ [group: string]: CalendarEvent[] }>((acc, next) => {
                if (!(next.group in acc)) acc[next.group] = [];
                acc[next.group].push(next);
                return acc;
            }, {}),
        [monthEvents],
    );

    const groupData = Object.entries(groupedMonthEvents)
        .sort(([a], [b]) => groupSort?.(a, b) ?? a.localeCompare(b))
        .map(([group, groupEvents]) => {
            const events = groupEvents
                .sort(({ interval: a }, { interval: b }) => compareAsc(a.start, b.start))
                .map((event, i) => (
                    <Event
                        key={i}
                        event={event}
                        view='quarter'
                        onClick={() => onSelected?.(event)}
                        draggable={event.draggable && !!onDnD}
                        onDragStart={e => (
                            (props.dragEvent.current = event), e.currentTarget.classList.add('dragging')
                        )}
                        onDragEnd={e => (
                            (props.dragEvent.current = undefined), e.currentTarget.classList.remove('dragging')
                        )}
                    />
                ));
            return { group, events };
        });

    return (
        <div
            className={classes.root}
            onDragEnter={e => (props.dragEvent.current || onExternalDnD) && e.currentTarget.classList.add('drag-over')}
            onDragOver={e => (props.dragEvent.current || onExternalDnD) && e.preventDefault()}
            onDragLeave={e => e.target === e.currentTarget && e.currentTarget.classList.remove('drag-over')}
            onDrop={e => {
                e.currentTarget.classList.remove('drag-over');
                if (!props.dragEvent.current) return onExternalDnD?.(e, props.month);
                const difference = differenceInCalendarMonths(props.month, props.dragEvent.current.interval.start);
                if (difference !== 0) onDnD?.(props.dragEvent.current, { months: difference });
            }}
        >
            <Tooltip classes={{ tooltip: yearMonthClasses.tooltip }} title={t('view-month')}>
                <Button
                    className={clsx(yearMonthClasses.header, classes.header)}
                    onClick={() => (setView('month'), setAnchor(props.month))}
                >
                    {t('month', { date: props.month })}
                    {monthEvents.length > 0 && (
                        <div className={clsx(yearMonthClasses.day, 'count')}>{monthEvents.length}</div>
                    )}
                </Button>
            </Tooltip>
            <div className={clsx(classes.events, 'scroll-shadow')} style={{ ['--shadow-offset-t' as any]: '29px' }}>
                {groupData.map(({ group, events }) => (
                    <React.Fragment key={group}>
                        <span>{group}</span>
                        {events}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};
